/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import {
  AfterViewInit,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CookieConsentService } from '@app/app/shared/services/cookie-consent/cookie-consent.service';
import { iframely } from '@iframely/embed.js';
import { ExternalContentSummary, MediaContent } from '../../../../models';

@Component({
  selector: 'ptl-external-content-summary',
  templateUrl: './external-content-summary.component.html',
  styleUrls: ['./external-content-summary.component.scss'],
})
export class ExternalContentSummaryComponent implements OnChanges, AfterViewInit {
  private _content: ExternalContentSummary | undefined;
  private _canRemoveItem = true;

  @Input()
  set content(value: ExternalContentSummary | undefined) {
    if (typeof value === 'string') {
      this._content = JSON.parse(decodeURIComponent(value));
    } else {
      this._content = value;
    }
  }

  get content() {
    return this._content;
  }

  @Input()
  set canRemoveItem(value) {
    if (typeof value === 'string') {
      this._canRemoveItem = JSON.parse(decodeURIComponent(value));
    } else {
      this._canRemoveItem = value;
    }
  }

  get canRemoveItem() {
    return this._canRemoveItem;
  }

  @Input() newEditor = false;

  /** Outputs removing of the component */
  @Output() removeContent = new EventEmitter<void>();

  /** Outputs the new position of the HTML element. */
  @Output() updateMediaPosition = new EventEmitter<string>();

  @Output() mediaContentReplaced = new EventEmitter<MediaContent>();

  @Output() regularLinkReplaced = new EventEmitter<string>();

  @ViewChild('oembedEl', { static: true }) public oembedEl: ElementRef | undefined;

  readonly embedsAllowed = inject(CookieConsentService).embeddedContentAllowed;

  private viewReady = false;

  constructor() {
    effect(() => {
      const allowed = this.embedsAllowed();
      if (!this.viewReady) {
        return;
      }
      if (allowed) {
        this.mountEmbed();
      } else {
        this.unmountEmbed();
      }
    });
  }

  onCloseClick() {
    this.removeContent.next();
  }

  ngAfterViewInit() {
    this.viewReady = true;
    this.mountEmbed();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes?.content &&
      !!changes.content.previousValue &&
      (changes.content.previousValue as ExternalContentSummary).html !== (changes.content.currentValue as ExternalContentSummary).html
    ) {
      this.unmountEmbed();
      this.mountEmbed();
    }
  }

  private mountEmbed(): void {
    const container = this.oembedEl?.nativeElement as HTMLElement | undefined;
    const html = this.content?.html;
    if (!container || !html || !this.embedsAllowed() || container.children.length > 0) {
      return;
    }
    // createContextualFragment enables the execution of <script> if present in our html
    const fragment = document.createRange().createContextualFragment(html);

    fragment.querySelectorAll('iframe').forEach((iframe) => {
      if (!iframe.hasAttribute('referrerpolicy')) {
        iframe.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');
      }
    });

    container.appendChild(fragment);
    setTimeout(() => {
      iframely.load();
    }, 100);
  }

  private unmountEmbed(): void {
    const container = this.oembedEl?.nativeElement as HTMLElement | undefined;
    if (container) {
      container.innerHTML = '';
    }
  }

  onUpdateMediaPosition(position: string): void {
    this.updateMediaPosition.emit(position);
  }

  onReplaceMediaContent(media: MediaContent): void {
    this.mediaContentReplaced.emit(media);
  }

  onReplaceRegularLink(link: string): void {
    this.regularLinkReplaced.emit(link);
  }

  openUrl() {
    if (this.content?.url) {
      window.open(this.content.url, '_self');
    }
  }
}
